import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Modal, Tooltip } from 'antd';
import { useSubscriptionStore, PaymentCard, CardBrand } from 'src/store/subscriptionStore';
import {
  useCreateSetupIntentMutation,
  useSavePaymentMethodMutation,
  useStripeConfigQuery,
  usePaymentMethodsQuery,
} from 'src/api/subscription/useSubscriptionApi';
import { toast } from 'src/utils/appAlert';
import { ReactComponent as CardSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/add-payment-method/card.svg';
import { ReactComponent as CvvHintSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/add-payment-method/cvv-hint.svg';
import { CardBrandLogo } from '../components/CardBrandLogo';
import { validateCardholderName } from 'src/utils/cardUtils';
import './SubscriptionModals.scss';

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardToEdit?: PaymentCard | null;
  onSuccess?: () => void;
}

type StripeField = 'cardNumber' | 'expiry' | 'cvc';

type StripeElementChange = {
  complete: boolean;
  error?: { message?: string };
  brand?: string;
};

type StripeFieldState = {
  complete: boolean;
  error: string;
};

const stripeElementOptions = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: 'Poppins, sans-serif',
      fontSize: '13px',
      fontSmoothing: 'antialiased',
      '::placeholder': {
        color: 'rgba(255, 255, 255, 0.3)',
      },
    },
    invalid: {
      color: '#ff8a8a',
    },
  },
};

const getCardBrand = (brand?: string): CardBrand => {
  const supportedBrands: CardBrand[] = [
    'visa',
    'mastercard',
    'amex',
    'discover',
    'jcb',
    'diners',
    'unionpay',
    'maestro',
  ];
  return supportedBrands.includes(brand as CardBrand) ? (brand as CardBrand) : 'generic';
};

const PaymentMethodForm: FC<PaymentMethodModalProps> = ({
  isOpen,
  onClose,
  cardToEdit,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const {
    addPaymentMethod,
    updatePaymentMethod,
    paymentMethods: storePaymentMethods,
  } = useSubscriptionStore();
  const { data: serverPaymentMethods } = usePaymentMethodsQuery(isOpen);
  const createSetupIntentMutation = useCreateSetupIntentMutation();
  const savePaymentMutation = useSavePaymentMethodMutation();

  const existingCards = serverPaymentMethods?.payment_methods ?? storePaymentMethods ?? [];
  const existingCardsCount = existingCards.length;
  // If user has 0 cards or is editing their only card (<= 1 card), it must be default and cannot be changed
  const isDefaultForced =
    existingCardsCount === 0 || (Boolean(cardToEdit) && existingCardsCount <= 1);

  const [cardholderName, setCardholderName] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const [cardBrand, setCardBrand] = useState<CardBrand>('generic');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [stripeFields, setStripeFields] = useState<Record<StripeField, StripeFieldState>>({
    cardNumber: { complete: false, error: '' },
    expiry: { complete: false, error: '' },
    cvc: { complete: false, error: '' },
  });

  const resetForm = useCallback(() => {
    setCardholderName('');
    setIsDefault(true);
    setCardBrand('generic');
    setErrors({});
    setTouched({});
    setStripeFields({
      cardNumber: { complete: false, error: '' },
      expiry: { complete: false, error: '' },
      cvc: { complete: false, error: '' },
    });
    if (elements) {
      elements.getElement(CardNumberElement)?.clear();
      elements.getElement(CardExpiryElement)?.clear();
      elements.getElement(CardCvcElement)?.clear();
    }
  }, [elements]);

  useEffect(() => {
    if (isOpen) {
      setCardholderName(cardToEdit?.cardholderName || '');
      setIsDefault(isDefaultForced ? true : (cardToEdit?.isDefault ?? false));
      setCardBrand(cardToEdit?.brand || 'generic');
      setErrors({});
      setTouched({});
      setStripeFields({
        cardNumber: { complete: Boolean(cardToEdit), error: '' },
        expiry: { complete: Boolean(cardToEdit), error: '' },
        cvc: { complete: Boolean(cardToEdit), error: '' },
      });
      if (!cardToEdit && elements) {
        elements.getElement(CardNumberElement)?.clear();
        elements.getElement(CardExpiryElement)?.clear();
        elements.getElement(CardCvcElement)?.clear();
      }
    } else {
      resetForm();
    }
  }, [isOpen, cardToEdit, resetForm, isDefaultForced, elements]);

  const handleStripeChange = (field: StripeField, event: StripeElementChange) => {
    const errorMessage = event.error?.message || '';
    setStripeFields((previous) => ({
      ...previous,
      [field]: { complete: event.complete, error: errorMessage },
    }));
    setErrors((previous) => ({ ...previous, [field]: errorMessage }));
    if (field === 'cardNumber' && event.brand) {
      setCardBrand(getCardBrand(event.brand));
    }
  };

  const handleStripeBlur = (field: StripeField) => {
    setTouched((previous) => ({ ...previous, [field]: true }));
  };

  const validateName = () => {
    const error = validateCardholderName(cardholderName.trim());
    setErrors((previous) => ({ ...previous, cardholderName: error }));
    setTouched((previous) => ({ ...previous, cardholderName: true }));
    return !error;
  };

  const handleSave = async () => {
    const nextErrors: Record<string, string> = {};

    if (!cardToEdit) {
      (['cardNumber', 'expiry', 'cvc'] as StripeField[]).forEach((field) => {
        if (!stripeFields[field].complete) {
          nextErrors[field] = stripeFields[field].error || 'Please complete this field.';
        }
      });
    }

    const nameError = validateCardholderName(cardholderName.trim());
    if (nameError) nextErrors.cardholderName = nameError;

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setTouched({ cardNumber: true, expiry: true, cvc: true, cardholderName: true });
      return;
    }

    try {
      let paymentMethodId = cardToEdit?.id;

      if (!cardToEdit) {
        if (!stripe || !elements) {
          toast.error('Secure card fields are still loading. Please try again.');
          return;
        }

        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
          toast.error('Secure card fields are unavailable. Please reopen the form.');
          return;
        }

        const setupIntent = await createSetupIntentMutation.mutateAsync();
        const confirmation = await stripe.confirmCardSetup(setupIntent.client_secret, {
          payment_method: {
            card: cardNumberElement,
            billing_details: { name: cardholderName.trim() },
          },
        });

        if (confirmation.error) {
          const message = confirmation.error.message || 'Could not validate your card.';
          setErrors((previous) => ({ ...previous, cardNumber: message }));
          setTouched((previous) => ({ ...previous, cardNumber: true }));
          toast.error(message);
          return;
        }

        const confirmedPaymentMethod = confirmation.setupIntent?.payment_method;
        paymentMethodId =
          typeof confirmedPaymentMethod === 'string'
            ? confirmedPaymentMethod
            : confirmedPaymentMethod?.id;

        if (confirmation.setupIntent?.status !== 'succeeded' || !paymentMethodId) {
          toast.error('The card could not be verified. Please try another card.');
          return;
        }
      }

      if (!paymentMethodId) {
        toast.error('A payment method is required.');
        return;
      }

      const savedCard = await savePaymentMutation.mutateAsync({
        payment_method_id: paymentMethodId,
        cardholder_name: cardholderName.trim(),
        is_default: isDefaultForced ? true : isDefault,
      });

      if (cardToEdit) {
        updatePaymentMethod(cardToEdit.id, {
          expiry: savedCard?.expiry || cardToEdit.expiry,
          cardholderName: cardholderName.trim(),
          isDefault: isDefaultForced ? true : isDefault,
        });
      } else {
        addPaymentMethod({
          brand: getCardBrand(savedCard?.brand) || cardBrand,
          last4: savedCard?.last4 || '',
          expiry: savedCard?.expiry || '',
          cardholderName: cardholderName.trim(),
          isDefault: isDefaultForced ? true : isDefault,
        });
      }

      toast.success('Payment method saved successfully.');
      onSuccess?.();
      onClose();
    } catch (error: any) {
      if (error?.fieldErrors) {
        setErrors(error.fieldErrors);
      }
      toast.error(error?.userMessage || 'Failed to save card. Please try again.');
    }
  };

  const handleModalClose = () => {
    resetForm();
    onClose();
  };

  const isSaving = createSetupIntentMutation.isPending || savePaymentMutation.isPending;
  const cardNumberError = touched.cardNumber ? errors.cardNumber : '';
  const expiryError = touched.expiry ? errors.expiry : '';
  const cvcError = touched.cvc ? errors.cvc : '';
  const nameError = touched.cardholderName ? errors.cardholderName : '';

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void handleSave();
      }}
      noValidate
    >
      <div className="modal-header">
        <h2>{cardToEdit ? 'Edit Payment Method' : 'Add Payment Method'}</h2>
        <p>{'Add a card to start your Pro subscription'}</p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#ffffff' }}>{'Card Details'}</span>
      </div>

      {/* Card Number */}
      <div className="form-group" style={{ marginBottom: 16 }}>
        <label htmlFor="modal-card-number">{'CARD NUMBER'}</label>
        {cardToEdit ? (
          <div className="input-wrapper">
            <input
              id="modal-card-number"
              type="text"
              value={`•••• •••• •••• ${cardToEdit.last4}`}
              readOnly
              aria-label="Saved card number"
            />
            <div className="input-icon-right">
              <CardBrandLogo
                brand={cardToEdit.brand}
                className="card-brand-preview"
                aria-label={`${cardToEdit.brand} card logo`}
              />
            </div>
          </div>
        ) : (
          <div className={`stripe-element-wrapper ${cardNumberError ? 'has-error' : ''}`}>
            <CardNumberElement
              id="modal-card-number"
              options={{ ...stripeElementOptions, placeholder: '1234 1234 1234 1234' }}
              onChange={(event) => handleStripeChange('cardNumber', event)}
              onBlur={() => handleStripeBlur('cardNumber')}
            />
            <div className="input-icon-right">
              {cardBrand !== 'generic' ? (
                <CardBrandLogo
                  brand={cardBrand}
                  className="card-brand-preview"
                  aria-label={`${cardBrand} card logo`}
                />
              ) : (
                <CardSVG className="card-generic-icon" />
              )}
            </div>
          </div>
        )}
        {cardNumberError && <span className="error-text">{cardNumberError}</span>}
      </div>

      {/* Expiry Date & CVC/CID */}
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="modal-card-expiry">{'EXPIRY DATE'}</label>
          {cardToEdit ? (
            <input id="modal-card-expiry" type="text" value={cardToEdit.expiry} readOnly />
          ) : (
            <div className={`stripe-element-wrapper ${expiryError ? 'has-error' : ''}`}>
              <CardExpiryElement
                id="modal-card-expiry"
                options={{ ...stripeElementOptions, placeholder: 'MM / YY' }}
                onChange={(event) => handleStripeChange('expiry', event)}
                onBlur={() => handleStripeBlur('expiry')}
              />
            </div>
          )}
          {expiryError && <span className="error-text">{expiryError}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="modal-card-cvc">{cardBrand === 'amex' ? 'CID' : 'CVC'}</label>
          {cardToEdit ? (
            <input id="modal-card-cvc" type="text" value="•••" readOnly />
          ) : (
            <div
              className={`stripe-element-wrapper with-icon-right ${cvcError ? 'has-error' : ''}`}
            >
              <CardCvcElement
                id="modal-card-cvc"
                options={{
                  ...stripeElementOptions,
                  placeholder: cardBrand === 'amex' ? '1234' : 'CVC',
                }}
                onChange={(event) => handleStripeChange('cvc', event)}
                onBlur={() => handleStripeBlur('cvc')}
              />
              <div className="input-icon-right">
                <Tooltip
                  title={
                    cardBrand === 'amex'
                      ? '4-digit CID security code on the front of your card'
                      : '3-digit security code on the back of your card'
                  }
                  placement="topRight"
                  rootClassName="cvv-tooltip-overlay"
                >
                  <button
                    type="button"
                    className="cvv-hint-btn"
                    aria-label={`${cardBrand === 'amex' ? 'CID' : 'CVC'} security code hint`}
                  >
                    <CvvHintSVG />
                  </button>
                </Tooltip>
              </div>
            </div>
          )}
          {cvcError && <span className="error-text">{cvcError}</span>}
        </div>
      </div>

      {/* Cardholder Name */}
      <div className="form-group" style={{ marginBottom: 16 }}>
        <label htmlFor="modal-cardholder-name">{'CARDHOLDER NAME'}</label>
        <input
          id="modal-cardholder-name"
          type="text"
          autoComplete="cc-name"
          value={cardholderName}
          maxLength={50}
          placeholder="Name on card"
          className={nameError ? 'has-error' : ''}
          aria-required="true"
          aria-invalid={!!nameError}
          onChange={(event) => setCardholderName(event.target.value)}
          onBlur={() => validateName()}
        />
        {nameError && <span className="error-text">{nameError}</span>}
      </div>

      {/* Default Checkbox */}
      <label className={`checkbox-group ${isDefaultForced ? 'disabled' : ''}`}>
        <input
          type="checkbox"
          checked={isDefaultForced ? true : isDefault}
          disabled={isDefaultForced}
          onChange={(event) => {
            if (!isDefaultForced) {
              setIsDefault(event.target.checked);
            }
          }}
        />
        <span>{'Set as default payment method'}</span>
      </label>

      {/* Actions */}
      <div className="modal-actions">
        <button type="button" className="btn-cancel" onClick={handleModalClose}>
          {'Cancel'}
        </button>
        <button type="submit" className="btn-save" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export const PaymentMethodModal: FC<PaymentMethodModalProps> = (props) => {
  const { isOpen, cardToEdit } = props;
  const { data: stripeConfig, isLoading, isError } = useStripeConfigQuery(isOpen);
  const stripePromise = useMemo(
    () => (stripeConfig?.publishable_key ? loadStripe(stripeConfig.publishable_key) : null),
    [stripeConfig],
  );

  return (
    <Modal
      open={isOpen}
      onCancel={props.onClose}
      footer={null}
      centered
      width={560}
      closeIcon={null}
      destroyOnHidden
      rootClassName="subscription-modal-wrapper"
      styles={{
        mask: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(6, 7, 35, 0.75)',
        },
      }}
    >
      {isOpen && isLoading ? (
        <div className="stripe-loading-message">{'Loading secure card fields...'}</div>
      ) : isOpen && isError && !cardToEdit ? (
        <div className="stripe-loading-message stripe-error-message">
          {'Unable to load secure card fields. Please close and try again.'}
        </div>
      ) : isOpen && stripePromise ? (
        <Elements stripe={stripePromise} key={isOpen ? 'open' : 'closed'}>
          <PaymentMethodForm {...props} />
        </Elements>
      ) : null}
    </Modal>
  );
};
